/**
 * MONSTER TOWN — WALKING THE TOWN. WORLD CREW (src/world/**).
 *
 * The town is one continuous place: out of the plot, across the crossing, down
 * the street, to the ice cream shop. That only works if a tap on a far-away
 * doorstep produces a route rather than a monster wading through a hedge, so
 * this file owns:
 *
 *   1. THE WALKABILITY MASK — static town rectangles (buildings, fence, pond,
 *      trees) baked once, PLUS the live `Grid` for anything the kid has built.
 *   2. A* + string pulling over a 1-unit nav grid.
 *   3. THE AUTOPILOT. It listens to `play:walkTo` / `play:interactRequested` on
 *      the bus and, when the straight line is blocked, feeds the walker a
 *      sequence of waypoints instead. It NEVER imports the monster crew: it
 *      re-emits the same public events, one leg at a time, and steps on
 *      `play:arrived`. When the straight line is already clear it does nothing
 *      at all, so the simple case stays exactly as the monster crew wrote it.
 *
 * The re-emit is deferred by one microtask on purpose: the input router emits
 * `play:walkTo` right after `input:tap` inside the same dispatch, and a
 * correction sent synchronously would be overwritten by it.
 */

import { engine } from '../core/engine'
import { bus } from '../core/events'
import type { Grid } from '../core/grid'
import type { InstanceId, SceneId, Vec3 } from '../core/types'
import { app } from '../app/context'
import type { Rect } from './buildings'
import { GRID_HALF } from './layout'

// ---------------------------------------------------------------------------
// the mask
// ---------------------------------------------------------------------------

const CELL = 1
const HALF = 50
const COUNT = (HALF * 2) / CELL
/** Half a monster, so routes do not clip corners. */
const CLEARANCE = 0.45

let blocked = new Uint8Array(COUNT * COUNT)
let townGrid: Grid | null = null
let townSceneId: SceneId | null = null

const idx = (gx: number, gz: number): number => gz * COUNT + gx
const cellX = (gx: number): number => -HALF + (gx + 0.5) * CELL
const cellZ = (gz: number): number => -HALF + (gz + 0.5) * CELL
const toGx = (x: number): number => Math.floor((x + HALF) / CELL)
const toGz = (z: number): number => Math.floor((z + HALF) / CELL)

export function buildNavMask(grid: Grid, sceneId: SceneId, blockers: readonly Rect[]): void {
  townGrid = grid
  townSceneId = sceneId
  blocked = new Uint8Array(COUNT * COUNT)
  for (let gz = 0; gz < COUNT; gz++) {
    const z = cellZ(gz)
    for (let gx = 0; gx < COUNT; gx++) {
      const x = cellX(gx)
      let hit = 0
      for (const b of blockers) {
        if (
          x > b.x0 - CLEARANCE &&
          x < b.x1 + CLEARANCE &&
          z > b.z0 - CLEARANCE &&
          z < b.z1 + CLEARANCE
        ) {
          hit = 1
          break
        }
      }
      blocked[idx(gx, gz)] = hit
    }
  }
}

/** Can a monster stand here? Static town mask + whatever the kid has built. */
export function isTownWalkable(x: number, z: number): boolean {
  const gx = toGx(x)
  const gz = toGz(z)
  if (gx < 0 || gz < 0 || gx >= COUNT || gz >= COUNT) return false
  if (blocked[idx(gx, gz)] === 1) return false
  // Inside the grid the kid's own building decides; there is nothing walkable
  // outside it (the monster's walker treats out-of-grid as solid).
  if (townGrid === null) return true
  if (Math.abs(x) >= GRID_HALF || Math.abs(z) >= GRID_HALF) return false
  return townGrid.isWorldWalkable(x, z)
}

/** Straight-line check, sampled. Used to skip routing when nothing is in the way. */
export function lineIsClear(a: Vec3, b: Vec3): boolean {
  const dx = b.x - a.x
  const dz = b.z - a.z
  const dist = Math.hypot(dx, dz)
  const steps = Math.max(1, Math.ceil(dist / 0.4))
  for (let i = 1; i <= steps; i++) {
    const t = i / steps
    if (!isTownWalkable(a.x + dx * t, a.z + dz * t)) return false
  }
  return true
}

/** Nearest standable point to `p`, searched outwards. Null if the town is full. */
export function snapToWalkable(p: Vec3): Vec3 | null {
  if (isTownWalkable(p.x, p.z)) return { x: p.x, y: 0, z: p.z }
  const gx = toGx(p.x)
  const gz = toGz(p.z)
  for (let r = 1; r <= 14; r++) {
    for (let dz = -r; dz <= r; dz++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue
        const x = cellX(gx + dx)
        const z = cellZ(gz + dz)
        if (isTownWalkable(x, z)) return { x, y: 0, z }
      }
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// A*
// ---------------------------------------------------------------------------

const gScore = new Float32Array(COUNT * COUNT)
const cameFrom = new Int32Array(COUNT * COUNT)
const closed = new Uint8Array(COUNT * COUNT)
const NEIGHBOURS: readonly (readonly [number, number, number])[] = [
  [1, 0, 1],
  [-1, 0, 1],
  [0, 1, 1],
  [0, -1, 1],
  [1, 1, 1.4142],
  [1, -1, 1.4142],
  [-1, 1, 1.4142],
  [-1, -1, 1.4142],
]

/** Tiny binary heap keyed by f-score. */
class Heap {
  private readonly items: number[] = []
  private readonly keys: number[] = []

  get size(): number {
    return this.items.length
  }

  clear(): void {
    this.items.length = 0
    this.keys.length = 0
  }

  push(item: number, key: number): void {
    this.items.push(item)
    this.keys.push(key)
    let i = this.items.length - 1
    while (i > 0) {
      const p = (i - 1) >> 1
      if ((this.keys[p] ?? 0) <= (this.keys[i] ?? 0)) break
      this.swap(i, p)
      i = p
    }
  }

  pop(): number {
    const top = this.items[0] ?? -1
    const lastItem = this.items.pop()
    const lastKey = this.keys.pop()
    if (this.items.length > 0 && lastItem !== undefined && lastKey !== undefined) {
      this.items[0] = lastItem
      this.keys[0] = lastKey
      let i = 0
      for (;;) {
        const l = i * 2 + 1
        const r = l + 1
        let m = i
        if (l < this.items.length && (this.keys[l] ?? 0) < (this.keys[m] ?? 0)) m = l
        if (r < this.items.length && (this.keys[r] ?? 0) < (this.keys[m] ?? 0)) m = r
        if (m === i) break
        this.swap(i, m)
        i = m
      }
    }
    return top
  }

  private swap(a: number, b: number): void {
    const ti = this.items[a] ?? 0
    const tk = this.keys[a] ?? 0
    this.items[a] = this.items[b] ?? 0
    this.keys[a] = this.keys[b] ?? 0
    this.items[b] = ti
    this.keys[b] = tk
  }
}

const heap = new Heap()

function octile(ax: number, az: number, bx: number, bz: number): number {
  const dx = Math.abs(ax - bx)
  const dz = Math.abs(az - bz)
  return dx + dz + (1.4142 - 2) * Math.min(dx, dz)
}

/**
 * A route from `from` to `to` as world-space waypoints, already straightened.
 * Returns null if there is no way through. Runs on tap only, never per frame.
 */
export function findWalkPath(from: Vec3, to: Vec3): Vec3[] | null {
  const start = snapToWalkable(from)
  const goal = snapToWalkable(to)
  if (start === null || goal === null) return null

  const sx = toGx(start.x)
  const sz = toGz(start.z)
  const tx = toGx(goal.x)
  const tz = toGz(goal.z)
  if (sx === tx && sz === tz) return [from, goal]

  gScore.fill(Infinity)
  closed.fill(0)
  cameFrom.fill(-1)
  heap.clear()

  const startIdx = idx(sx, sz)
  gScore[startIdx] = 0
  heap.push(startIdx, octile(sx, sz, tx, tz))
  const goalIdx = idx(tx, tz)
  let found = false
  let guard = 0

  while (heap.size > 0 && guard++ < COUNT * COUNT * 2) {
    const cur = heap.pop()
    if (cur === goalIdx) {
      found = true
      break
    }
    if (closed[cur] === 1) continue
    closed[cur] = 1
    const cx = cur % COUNT
    const cz = (cur - cx) / COUNT
    const base = gScore[cur] ?? Infinity

    for (const [dx, dz, cost] of NEIGHBOURS) {
      const nx = cx + dx
      const nz = cz + dz
      if (nx < 0 || nz < 0 || nx >= COUNT || nz >= COUNT) continue
      const ni = idx(nx, nz)
      if (closed[ni] === 1) continue
      if (!isTownWalkable(cellX(nx), cellZ(nz))) continue
      // No cutting a corner between two blocked cells.
      if (dx !== 0 && dz !== 0) {
        if (!isTownWalkable(cellX(cx + dx), cellZ(cz))) continue
        if (!isTownWalkable(cellX(cx), cellZ(cz + dz))) continue
      }
      const next = base + cost
      if (next >= (gScore[ni] ?? Infinity)) continue
      gScore[ni] = next
      cameFrom[ni] = cur
      heap.push(ni, next + octile(nx, nz, tx, tz))
    }
  }

  if (!found) return null

  const cells: number[] = []
  let node = goalIdx
  let hops = 0
  while (node !== -1 && hops++ < COUNT * COUNT) {
    cells.push(node)
    if (node === startIdx) break
    node = cameFrom[node] ?? -1
  }
  cells.reverse()

  const raw: Vec3[] = [{ x: from.x, y: 0, z: from.z }]
  for (const c of cells) {
    const gx = c % COUNT
    const gz = (c - gx) / COUNT
    raw.push({ x: cellX(gx), y: 0, z: cellZ(gz) })
  }
  raw.push(goal)
  return straighten(raw)
}

/** Drop every waypoint the monster can simply walk past. */
function straighten(points: readonly Vec3[]): Vec3[] {
  if (points.length <= 2) return [...points]
  const out: Vec3[] = []
  const first = points[0]
  if (first === undefined) return [...points]
  out.push(first)
  let anchor = first
  for (let i = 1; i < points.length - 1; i++) {
    const next = points[i + 1]
    const here = points[i]
    if (next === undefined || here === undefined) continue
    if (!lineIsClear(anchor, next)) {
      out.push(here)
      anchor = here
    }
  }
  const last = points[points.length - 1]
  if (last !== undefined) out.push(last)
  return out
}

// ---------------------------------------------------------------------------
// autopilot
// ---------------------------------------------------------------------------

let enabled = true
let driving = false
let selfEmit = false
let claimed = false
let queue: Vec3[] = []
let pendingInteract: InstanceId | null = null
let onArrive: (() => void) | null = null

/** Current leg, for the stall watchdog below. */
let legTarget: Vec3 | null = null
let lastX = 0
let lastZ = 0
let stalledFor = 0
let advancing = false
/** Close enough to a waypoint to call it reached, whatever the walker thinks. */
const LEG_REACHED = 0.85
/** How long a motionless monster waits before the route gives it a nudge. */
const STALL_SECONDS = 0.5

/** Turn routing off (the monster crew can adopt `findWalkPath` and take over). */
export function setNavAutopilot(on: boolean): void {
  enabled = on
  if (!on) reset()
}

/**
 * Tell the autopilot that the tap currently being dispatched is already spoken
 * for (a door hotspot), so the router's follow-up `play:walkTo` is ignored.
 */
export function claimTap(): void {
  claimed = true
}

function reset(): void {
  driving = false
  queue = []
  pendingInteract = null
  onArrive = null
  legTarget = null
  stalledFor = 0
}

function emit(fn: () => void): void {
  selfEmit = true
  try {
    fn()
  } finally {
    selfEmit = false
  }
}

function stepRoute(): void {
  if (!driving || townSceneId === null || advancing) return
  advancing = true
  try {
    advance()
  } finally {
    advancing = false
  }
}

function advance(): void {
  const next = queue.shift()
  if (next !== undefined) {
    legTarget = next
    stalledFor = 0
    lastX = app.playerPos.x
    lastZ = app.playerPos.z
    emit(() => {
      if (townSceneId !== null) bus.emit('play:walkTo', { sceneId: townSceneId, target: next })
    })
    return
  }
  legTarget = null
  const interact = pendingInteract
  const arrive = onArrive
  reset()
  if (interact !== null && townSceneId !== null) {
    const sceneId = townSceneId
    emit(() => bus.emit('play:interactRequested', { instanceId: interact, sceneId }))
  }
  if (arrive !== null) arrive()
}

function route(target: Vec3, interactId: InstanceId | null, arrive: (() => void) | null): void {
  if (!enabled || townSceneId === null) return
  const goal = snapToWalkable(target)
  if (goal === null) {
    reset()
    return
  }
  const from = app.playerPos
  const clear = lineIsClear(from, goal)
  if (clear && arrive === null) {
    // Nothing in the way: leave the walker exactly as the monster crew wrote it.
    reset()
    return
  }
  const path = clear ? [from, goal] : findWalkPath(from, goal)
  if (path === null || path.length < 2) {
    reset()
    return
  }
  queue = path.slice(1)
  pendingInteract = interactId
  onArrive = arrive
  driving = true
  // Deferred: the router emits its own `play:walkTo` after this dispatch.
  queueMicrotask(stepRoute)
}

/** Walk to a point and run `arrive` when the monster gets there. */
export function navGoTo(target: Vec3, arrive?: () => void): void {
  route(target, null, arrive ?? null)
}

/**
 * THE STALL WATCHDOG. A walker that decelerates asymptotically can settle a
 * hair OUTSIDE its arrive radius and never emit `play:arrived`, which leaves a
 * routed monster standing in the street forever — the worst possible outcome in
 * a game with no fail states. So the route never depends on that event alone:
 * it also advances when the monster is near enough to the leg, or when it has
 * simply stopped moving. Waypoints are guides, not gates.
 */
function watchdog(dt: number): void {
  if (!driving || legTarget === null) return
  const p = app.playerPos
  const d = Math.hypot(p.x - legTarget.x, p.z - legTarget.z)
  if (d < LEG_REACHED) {
    stepRoute()
    return
  }
  const moved = Math.hypot(p.x - lastX, p.z - lastZ)
  lastX = p.x
  lastZ = p.z
  if (moved < 0.01 * (dt / SIM_STEP)) {
    stalledFor += dt
    if (stalledFor > STALL_SECONDS) stepRoute()
  } else {
    stalledFor = 0
  }
}

/** The engine's fixed step, so the watchdog's timing is frame-rate free. */
const SIM_STEP = 1 / 30

export function installNav(grid: Grid, sceneId: SceneId, blockers: readonly Rect[]): void {
  buildNavMask(grid, sceneId, blockers)

  engine.addSystem({
    id: 'world.nav',
    order: 46,
    fixed: (dt) => watchdog(dt),
  })

  bus.on('play:walkTo', (e) => {
    if (selfEmit) return
    if (claimed) {
      claimed = false
      return
    }
    if (e.sceneId !== townSceneId) return
    route(e.target, null, null)
  })

  bus.on('play:interactRequested', (e) => {
    if (selfEmit || e.sceneId !== townSceneId) return
    const node = townGrid
    if (node === null) return
    const p = node.placement(e.instanceId)
    if (p === null) return
    const centre = node.footprintCenter(p.anchor, p.footprint, p.rot)
    if (lineIsClear(app.playerPos, centre)) return
    route(centre, e.instanceId, null)
  })

  bus.on('play:arrived', () => {
    if (driving) stepRoute()
  })

  bus.on('app:modeChanged', (e) => {
    if (e.mode === 'build') reset()
  })
  bus.on('scene:left', () => reset())

  /**
   * "SOMEWHERE THIS MONSTER COULD ACTUALLY WALK TO."
   *
   * The first-run tutorial's opening beat points a paw at a patch of ground and
   * waits for a kid to tap it. Only this file knows which patches are ground:
   * the town's buildings, ponds and plaza furniture are baked statics that the
   * placement grid has never heard of, so a HUD-side guess lands the paw on a
   * roof about a third of the time. Crews never import each other, so it asks —
   * synchronously, exactly like `world:landmarksRequest`.
   *
   * Eight directions at two radii, nearest first, and the whole straight line
   * has to be clear so the walk is a walk and not a route around a house. A null
   * reply is honest: the caller falls back to its own guess rather than pointing
   * at nothing.
   */
  bus.on('world:walkSpotRequest', (e) => {
    const from: Vec3 = { x: e.from.x, y: 0, z: e.from.z }
    let best: Vec3 | null = null
    for (const radius of [4.5, 6.5, 3]) {
      for (let i = 0; i < 8 && best === null; i++) {
        const a = (i / 8) * Math.PI * 2
        const p: Vec3 = { x: from.x + Math.cos(a) * radius, y: 0, z: from.z + Math.sin(a) * radius }
        if (!isTownWalkable(p.x, p.z)) continue
        if (!lineIsClear(from, p)) continue
        best = p
      }
      if (best !== null) break
    }
    bus.emit('world:walkSpot', { at: best })
  })
}

/** Debug view for the harness. */
export function navStats(): { cells: number; blocked: number; driving: boolean; queued: number } {
  let n = 0
  for (let i = 0; i < blocked.length; i++) if (blocked[i] === 1) n++
  return { cells: blocked.length, blocked: n, driving, queued: queue.length }
}
